import { FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Coins, Pencil, Trash2 } from 'lucide-react';
import { FraisTunnelListItemDto } from 'api/funnel';
import { Sizes } from 'enums/sizes';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { useFunnelDetail } from '../FunnelDetailContext';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { RowActions } from 'back-office/shared/components/RowActions';
import { FeeModal } from '../forms/FeeModal';
import { DASH } from '../funnelFormat';

/** Un frais sans bénéficiaire nommé est encaissé par le courtier qui contractualise. */
const isConnectedBroker = (fee: FraisTunnelListItemDto) => !fee.beneficiaire;

export const FeesBlock: FC = () => {
  const { tunnelId, funnel, expressionVariables, invalidatePricing } = useFunnelDetail();
  const { openBlankDialog } = useDialog();
  const { createMenuItem } = useActionMenu();
  const [toDelete, setToDelete] = useState<FraisTunnelListItemDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFees = useCallback(() => funnelsAPI.getFunnelFees({ tunnelId }), [tunnelId]);
  const { data, isLoading, error, reload } = useAsyncResource(fetchFees);

  // Un frais entre dans le prix client : sa modification périme la tarification.
  const refresh = useCallback(async () => {
    await reload();
    invalidatePricing();
  }, [reload, invalidatePricing]);

  const guarantees = useMemo(
    () =>
      (funnel.garanties ?? []).map((guarantee) => ({
        id: guarantee.id,
        label: guarantee.designation || 'Garantie sans désignation',
      })),
    [funnel.garanties]
  );

  const openFee = (fee?: FraisTunnelListItemDto) =>
    openBlankDialog(
      { size: Sizes['5xl'] },
      <FeeModal
        tunnelId={tunnelId}
        fraisTunnelId={fee?.id}
        title={fee ? `Modifier « ${fee.typeFrais ?? ''} »` : 'Ajouter un frais au tunnel'}
        guarantees={guarantees}
        variables={expressionVariables}
        onSuccess={refresh}
      />
    );

  const deleteFee = async () => {
    if (!toDelete) return;

    setIsDeleting(true);
    try {
      const response = await funnelsAPI.deleteFunnelFee({ id: toDelete.id });
      if (response.isSuccess) {
        toast.success('Frais supprimé');
        await refresh();
      } else {
        toast.error(response.errorMessage || 'La suppression a échoué');
      }
    } catch (err) {
      console.error('Suppression du frais impossible', err);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeleting(false);
      setToDelete(null);
    }
  };

  const rows = data?.frais ?? [];

  return (
    <>
      <BlockCard
        title="Frais"
        description="Rémunérations prélevées en complément de la prime"
        icon={<Coins className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {rows.length}
          </span>
        }
        actions={[createMenuItem('add', 'Ajouter un frais', () => openFee(), { icon: 'add' })]}
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        emptyLabel="Aucun frais sur ce tunnel"
        flushBody
      >
        <StripedList>
          {rows.map((fee) => (
            <li key={fee.id} className="flex items-center gap-4 px-5 py-2.5">
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-800">
                {fee.typeFrais || DASH}
              </span>

              {fee.displayedOnPricing === false && (
                <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  Masqué en tarification
                </span>
              )}

              <span className="min-w-0 flex-1 truncate text-sm">
                {isConnectedBroker(fee) ? (
                  <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                    Courtier connecté
                  </span>
                ) : (
                  <span className="text-gray-600">{fee.beneficiaire}</span>
                )}
              </span>

              <RowActions
                actions={[
                  {
                    tone: 'edit',
                    label: 'Modifier le frais',
                    icon: <Pencil className="h-4 w-4" />,
                    onClick: () => openFee(fee),
                  },
                  {
                    tone: 'danger',
                    label: 'Supprimer le frais',
                    icon: <Trash2 className="h-4 w-4" />,
                    onClick: () => setToDelete(fee),
                  },
                ]}
              />
            </li>
          ))}
        </StripedList>
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={deleteFee}
        isLoading={isDeleting}
        title="Supprimer ce frais ?"
        description={`Le frais « ${toDelete?.typeFrais ?? ''} » sera retiré du tunnel.`}
        confirmLabel="Supprimer"
      />
    </>
  );
};
